import { memo, useMemo, FC } from "react";
import { css, cx } from "@/domains/emotion";
import { EmotionClassStyles } from "@/domains/emotion/types";
import { MdsDropdownContentList, MdsDropdownItem } from "@/design-system/components/dropdown";
import { mdsDropdownItemComponentMapper } from "@/design-system/components/dropdown/MdsDropdownItemComponent";
import { ZIndex } from "@/domains/design/constants";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";

export interface MdsDropdownContentProps extends EmotionClassStyles {
  isOpen?: boolean;
  contentList: MdsDropdownContentList;
  rowStyles?: string;
  onHover?: ({ isHovering, itemId }: { isHovering?: boolean; itemId?: string }) => void;
  onOpenChange?: (value: boolean) => void;
  listRef?: React.RefObject<HTMLDivElement>;
}

const SectionDivider = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.border.default};
`;

const ContentContainer = styled.div({
  padding: 0,
  margin: 0,

  // When we need to scroll because content doesn't fit in the viewport.
  overflowY: "scroll",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
});

let dropdownTooltipCount = 0;

const generateDropdownTooltipId = () => {
  dropdownTooltipCount++;
  return `mds-dropdown-tooltip-${dropdownTooltipCount}`;
};

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const MdsDropdownContent: FC<MdsDropdownContentProps> = memo(
  ({ className, contentList, onHover, onOpenChange, listRef, rowStyles }) => {
    const tooltipId = useMemo(() => generateDropdownTooltipId(), []);
    const dropdownComponents = useMemo(
      () =>
        contentList.items.map((item: MdsDropdownItem) =>
          mdsDropdownItemComponentMapper({
            item,
            onOpenChange,
            tooltipId,
            onHover,
            className: rowStyles,
          })
        ),
      [contentList.items, onHover, onOpenChange, rowStyles, tooltipId]
    );

    const theme = useTheme();
    const contentStyles = css({
      display: "flex",
      flexDirection: "column",
      flexWrap: "nowrap",
      width: "max-content",
      minWidth: "176px",
      height: "100%",
      paddingBlock: theme.spacing["3"],
      borderRadius: theme.borderRadius.large,
      boxShadow: theme.shadows.surface,
      backgroundColor: theme.colors.surface.menu,
      zIndex: ZIndex.Dropdown,
      userSelect: "none",
      overflow: "hidden",
    });
    const containerStyles = cx(contentStyles, className);

    return (
      <>
        <div
          ref={listRef}
          onMouseEnter={() => onHover?.({ isHovering: true })}
          onMouseLeave={() => onHover?.({ isHovering: false })}
          className={containerStyles}
        >
          {/* Header */}
          {contentList.header && (
            <>
              {contentList.header.children}
              {!contentList.header.hideDivider && <SectionDivider />}
            </>
          )}

          {/* Content */}
          <ContentContainer>{dropdownComponents}</ContentContainer>

          {/* Footer */}
          {contentList.footer && (
            <>
              {dropdownComponents.length > 0 && <SectionDivider />}
              {contentList.footer.children}
            </>
          )}
        </div>
      </>
    );
  }
);
